import { useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Clock } from '@/components/icons'
import dayjs from 'dayjs'
import { Input } from './ui/input'
import { cn } from '@/lib/utils'

interface TimeInputProps {
  value: Date | null | undefined
  // Called with null when the user empties the field (web text variant only) —
  // callers must clear their state so required-validation can kick in.
  onChange: (date: Date | null) => void
  className?: string
  disabled?: boolean
  readOnly?: boolean
  timezone?: string
  // Explicit right-decoration text (e.g. "UTC", "UTC+3"); overrides the
  // Intl-derived label from `timezone`.
  tzLabel?: string
  hideClockIcon?: boolean
  // Always use the typed text field, even inside Capacitor (skip the native
  // wheel picker) — e.g. flight-log times pilots key in from the folio.
  keyboard?: boolean
}

// Native `<input type="time">` is used only inside the Capacitor app, where iOS
// shows its native wheel picker (unless the caller passes `keyboard`). In
// browsers we render a plain text field with
// lenient HH:mm parsing: desktop Safari's time input is unusable — keyboard
// segment edits never commit to `.value` (stuck badInput), and empty fields
// render the current time as placeholder so they look filled when they're not.
const isNativePlatform = Capacitor.isNativePlatform()

function useTzLabel(timezone: string | undefined, value: Date | null | undefined) {
  return timezone
    ? new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        // 'shortOffset' always yields "GMT±N" (never named zones like "PST"),
        // relabeled to match formatUtcOffsetLabel's "UTC±N".
        timeZoneName: 'shortOffset',
      })
        .formatToParts(value ?? new Date())
        .find((p) => p.type === 'timeZoneName')
        ?.value.replace(/^GMT/, 'UTC')
    : null
}

function NativeTimeInput({ value, onChange, className, disabled, readOnly, timezone, tzLabel, hideClockIcon }: TimeInputProps) {
  const timeValue = value ? dayjs(value).format('HH:mm') : ''

  const update = (target: HTMLInputElement) => {
    const timeString = target.value
    if (!timeString) return

    const [hours, minutes] = timeString.split(':').map(Number)
    const current = value || new Date()
    const updated = dayjs(current).hour(hours).minute(minutes).toDate()
    onChange(updated)
  }

  const derivedTzLabel = useTzLabel(timezone, value)
  const label = tzLabel ?? derivedTzLabel

  return (
    <Input
      type="time"
      readOnly={readOnly}
      value={timeValue}
      onChange={(e) => !readOnly && update(e.target)}
      onBlur={(e) => !readOnly && update(e.target as HTMLInputElement)}
      onInput={(e) => !readOnly && update(e.target as HTMLInputElement)}
      disabled={disabled}
      decorationLeft={hideClockIcon ? undefined : <Clock className="w-5 h-5" />}
      decorationRight={
        label ? (
          <span className="text-xs text-content-primary-subtle">{label}</span>
        ) : undefined
      }
      className={cn(
        'cursor-text [&_input]:w-18',
        readOnly &&
          'pointer-events-none border-transparent bg-surface-L2 shadow-none',
        className
      )}
    />
  )
}

// Live input mask: formats the draft as the user types so digits render as a
// time instead of raw text — "1231" → "12:31", "930" → "9:30" (first two
// digits can't be an hour), extra digits are dropped. A user-typed separator
// is respected ("9:" stays while the minutes are pending).
function formatTimeDraft(raw: string): string {
  const text = raw.replace(/[.,;]/g, ':')
  const colonIdx = text.indexOf(':')
  if (colonIdx !== -1) {
    const h = text.slice(0, colonIdx).replace(/\D/g, '').slice(0, 2)
    const m = text.slice(colonIdx + 1).replace(/\D/g, '').slice(0, 2)
    return `${h}:${m}`
  }
  const d = text.replace(/\D/g, '').slice(0, 4)
  if (d.length <= 2) return d
  if (Number(d.slice(0, 2)) <= 23) return `${d.slice(0, 2)}:${d.slice(2)}`
  return `${d[0]}:${d.slice(1, 3)}`
}

// Lenient 24h parse: "14:07", "14.07", "1407", "930", "9:5", "14", "9".
// Returns null when the text doesn't resolve to a valid time.
export function parseTimeText(raw: string): { hours: number; minutes: number } | null {
  const text = raw.trim().replace(/[.,;]/g, ':')
  if (!text) return null

  let hours: number
  let minutes: number
  if (text.includes(':')) {
    const [h, m = '0'] = text.split(':')
    if (!/^\d{1,2}$/.test(h.trim()) || !/^\d{1,2}$/.test(m.trim() || '0')) return null
    hours = Number(h)
    minutes = Number(m || '0')
  } else {
    if (!/^\d{1,4}$/.test(text)) return null
    if (text.length <= 2) {
      hours = Number(text)
      minutes = 0
    } else {
      // "930" -> 9:30, "1407" -> 14:07
      hours = Number(text.slice(0, text.length - 2))
      minutes = Number(text.slice(-2))
    }
  }

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
  return { hours, minutes }
}

function TextTimeInput({ value, onChange, className, disabled, readOnly, timezone, tzLabel, hideClockIcon }: TimeInputProps) {
  const committed = value ? dayjs(value).format('HH:mm') : ''
  // Draft is non-null only while the field is focused; outside editing the
  // display always reflects the committed prop value.
  const [draft, setDraft] = useState<string | null>(null)
  // Keeps the select-all from onFocus alive through the mouseup that follows a
  // click-to-focus (which would otherwise collapse the selection to a caret).
  const justFocusedRef = useRef(false)

  // Commits whenever the draft parses — including live while typing, so form
  // gates (e.g. the wizard's Next button) enable without waiting for blur,
  // matching how the native input behaved. Invalid/partial drafts are ignored.
  const commit = (raw: string) => {
    const parsed = parseTimeText(raw)
    if (!parsed) return
    const current = value || new Date()
    const updated = dayjs(current)
      .hour(parsed.hours)
      .minute(parsed.minutes)
      .second(0)
      .millisecond(0)
      .toDate()
    onChange(updated)
  }

  const derivedTzLabel = useTzLabel(timezone, value)
  const label = tzLabel ?? derivedTzLabel

  return (
    <Input
      type="text"
      inputMode="numeric"
      placeholder="HH:MM"
      maxLength={5}
      readOnly={readOnly}
      value={draft ?? committed}
      onFocus={(e) => {
        if (readOnly) return
        setDraft(committed)
        justFocusedRef.current = true
        e.target.select()
      }}
      onMouseUp={(e) => {
        if (justFocusedRef.current) {
          e.preventDefault()
          justFocusedRef.current = false
        }
      }}
      onChange={(e) => {
        if (readOnly) return
        const masked = formatTimeDraft(e.target.value)
        setDraft(masked)
        if (masked === '') {
          onChange(null) // explicit clear — surface it so the form can invalidate
        } else {
          commit(masked)
        }
      }}
      onBlur={() => {
        if (readOnly) return
        if (draft !== null && draft !== committed) commit(draft)
        setDraft(null)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      disabled={disabled}
      decorationLeft={hideClockIcon ? undefined : <Clock className="w-5 h-5" />}
      decorationRight={
        label ? (
          <span className="text-xs text-content-primary-subtle">{label}</span>
        ) : undefined
      }
      className={cn(
        'cursor-text [&_input]:w-18',
        readOnly &&
          'pointer-events-none border-transparent bg-surface-L2 shadow-none',
        className
      )}
    />
  )
}

export function TimeInput(props: TimeInputProps) {
  return isNativePlatform && !props.keyboard ? <NativeTimeInput {...props} /> : <TextTimeInput {...props} />
}
